'use client'

import { CollectionNavAction, WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import {
  CollectionNavCarouselData,
  CollectionNavCarouselOption,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'
import { useRouter } from 'next-nprogress-bar'
import React, { useCallback, useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import { SubCollectionCardList } from '@src/pages/[...slug]/components/collection/molecules/subCollectionCardList'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

const EMPTY_OPTIONS: CollectionNavCarouselOption[] = []

const CollectionNavCarouselWidgetComponent = (props: PLTypedWidgetProps<CollectionNavCarouselData>) => {
  const router = useRouter()
  const options = props.data.collectionNavCarouselData?.options ?? EMPTY_OPTIONS

  const optionsByIdMap = useMemo(() => new Map(options.map(option => [option.id, option])), [options])

  const subCollectionCards: SubCollection[] = useMemo(
    () =>
      options.map(
        option =>
          new SubCollection({
            id: option.id,
            title: option.displayName,
            slug: option.slug,
            seoTitle: option.displayName,
            subTitle: undefined,
            showAllColors: true,
            noIndex: false,
            productCards: undefined,
            marketingPanelBase: undefined,
            marketingPanelMobile: undefined,
            imageDesktop: new ImageDomain({
              alt: option.image?.alt ?? '',
              height: option.image?.height ?? 0,
              width: option.image?.width ?? 0,
              url: option.image?.url ?? '',
              id: '',
              fileName: '',
              displayModelInfo: false,
            }),
            additionalAttributes: undefined,
            navigationId: undefined,
            revSortedIdColor: undefined,
            totalProducts: undefined,
          }),
      ),
    [options],
  )

  const filterAvailable = useMemo(() => options.map(option => option.displayName), [options])

  const onCardClick = useCallback(
    (subCollection: SubCollection) => {
      const option = optionsByIdMap.get(subCollection.id.toString())
      if (!option || option.collectionNavAction !== CollectionNavAction.NAVIGATE) return

      trackEvent(TELEMETRY_EVENT_NAMES.SIMILAR_COLLECTION_CLICKED, {
        filter_count: filterAvailable.length,
        filter_used: option.displayName,
        filter_available: filterAvailable,
      })
      router.push(option.slug)
    },
    [filterAvailable, optionsByIdMap, router],
  )

  const isSubCollectionSelected = useCallback(
    (subCollection: SubCollection) => {
      const option = optionsByIdMap.get(subCollection.id.toString())
      return option?.selected ?? false
    },
    [optionsByIdMap],
  )

  if (subCollectionCards.length === 0) return null

  return (
    // PL nav carousel drives navigation via per-card option slugs in `onCardClick`.
    // This list-level widget does not use "explore everything" metadata.
    <SubCollectionCardList
      subCollections={subCollectionCards}
      isSubCollectionSelected={isSubCollectionSelected}
      onCardClick={onCardClick}
      trackSubcollectionCardClick={false}
      slug={''}
      collectionExploreEverythingImage={undefined}
      contentful_id={''}
    />
  )
}

export const CollectionNavCarouselWidget: PLTypedWidgetInterface<
  CollectionNavCarouselData,
  WidgetType.COLLECTION_NAV_CAROUSEL
> = {
  id: WidgetType.COLLECTION_NAV_CAROUSEL,
  errorBoundary: PLBaseErrorBoundary,
  render: CollectionNavCarouselWidgetComponent,
}
